import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { client } from '@/api/client';
import { useAuth } from '@/lib/AuthContext';
import { useAIDispatch } from '@/lib/AIDispatchContext';
import { toast } from 'sonner';
import { Sparkles, X, Send, Loader2, MapPin, DollarSign, CheckCircle, Phone } from 'lucide-react';
import { fetchMarketLoads } from '@/lib/marketLoads';
import { cn } from '@/lib/utils';
import { checkBookingEligibility } from '@/lib/driverEligibility';

const QUICK_PROMPTS = [
  'Best load for today',
  'Anything over $14/mile',
  'I want to be home by Friday',
  'Compare my top 2 options',
];

// Extract load numbers mentioned in a Claude response
function extractLoadNumbers(text, availableLoads) {
  return availableLoads
    .filter(l => l.load_number && text.includes(l.load_number))
    .map(l => l.load_number);
}

function InlineLoadCard({ load, onBook, booking }) {
  const rpm = load.miles && load.rate ? (load.rate / load.miles).toFixed(2) : null;
  const isMarket = load.source === 'Market';
  return (
    <div className={cn(
      "mt-2 rounded-xl border p-3 text-xs space-y-2",
      isMarket ? "border-amber-500/30 bg-amber-500/5" : "border-primary/30 bg-primary/5"
    )}>
      <div className="flex items-center justify-between gap-2">
        <span className={cn("font-bold text-sm", isMarket ? "text-amber-400" : "text-primary")}>#{load.load_number}</span>
        <div className="flex items-center gap-1.5">
          <span className={cn(
            "text-[10px] font-semibold px-1.5 py-0.5 rounded-full",
            isMarket ? "bg-amber-500/20 text-amber-300" : "bg-primary/20 text-primary"
          )}>
            {isMarket ? 'Market' : 'Company'}
          </span>
          <span className="text-muted-foreground">{load.equipment_required}</span>
        </div>
      </div>
      <div className="flex items-center gap-1.5 text-foreground">
        <MapPin className="w-3 h-3 text-green-400 flex-shrink-0" />
        <span>{load.origin}</span>
        <span className="text-muted-foreground">→</span>
        <span>{load.destination}</span>
      </div>
      <div className="flex items-center gap-3 text-muted-foreground">
        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${(load.rate || 0).toLocaleString()}</span>
        {load.miles && <span>{load.miles} mi</span>}
        {rpm && <span className={cn("font-semibold", isMarket ? "text-amber-400" : "text-primary")}>${rpm}/mi</span>}
        {load.pickup_date && <span>{load.pickup_date}</span>}
      </div>
      {isMarket ? (
        <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-2.5 py-2 space-y-1">
          <p className="text-amber-300 font-semibold">{load.broker_name}</p>
          <a
            href={`tel:${load.broker_phone}`}
            className="flex items-center gap-1.5 text-amber-200 hover:text-amber-100 transition-colors"
          >
            <Phone className="w-3 h-3" />{load.broker_phone}
          </a>
          <p className="text-muted-foreground text-[10px]">Call broker to book this load</p>
        </div>
      ) : (
        <button
          onClick={() => onBook(load)}
          disabled={booking === load.id}
          className="w-full flex items-center justify-center gap-2 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          {booking === load.id
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Booking...</>
            : <><CheckCircle className="w-3 h-3" /> Book This Load</>}
        </button>
      )}
    </div>
  );
}

function MessageBubble({ msg, availableLoads, onBook, booking }) {
  const isUser = msg.role === 'user';
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] px-3.5 py-2.5 rounded-2xl rounded-br-md bg-primary text-primary-foreground text-sm leading-relaxed">
          {msg.content}
        </div>
      </div>
    );
  }

  const mentionedNums = extractLoadNumbers(msg.content, availableLoads);
  const mentionedLoads = mentionedNums
    .map(num => availableLoads.find(l => l.load_number === num))
    .filter(Boolean);

  return (
    <div className="flex justify-start">
      <div className="max-w-[92%]">
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-3 h-3 text-primary" />
          </div>
          <span className="text-xs text-muted-foreground font-medium">Dispatch AI</span>
        </div>
        <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-md bg-white/8 border border-border text-sm text-foreground leading-relaxed whitespace-pre-wrap">
          {msg.content}
        </div>
        {mentionedLoads.map(load => (
          <InlineLoadCard key={load.id} load={load} onBook={onBook} booking={booking} />
        ))}
      </div>
    </div>
  );
}

export default function AIDispatchChat({ driverProfile }) {
  const { user } = useAuth();
  // Chat state lifted to AIDispatchContext: persists across client-side
  // navigation, resets on a full browser refresh. Local state below is
  // only for transient/per-mount UI (input text, request in-flight,
  // booking action, DOM refs).
  const {
    open,           setOpen,
    messages,       setMessages,
    systemPrompt,   setSystemPrompt,
    contextLoaded,  setContextLoaded,
    availableLoads, setAvailableLoads,
    btnPos,
  } = useAIDispatch();

  // Draggable button — DOM refs stay local (they bind to this mount's
  // button element). btnPos itself lives in context so the position
  // survives navigation across pages.
  const btnRef = useRef(null);
  const dragging = useRef(false);
  const didDrag = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Load saved position on mount and position the button
  useEffect(() => {
    try {
      const s = localStorage.getItem('dispatch_chat_btn_pos');
      if (s) btnPos.current = JSON.parse(s);
    } catch {}
    if (btnRef.current) {
      btnRef.current.style.left = btnPos.current.x + 'px';
      btnRef.current.style.top = btnPos.current.y + 'px';
    }
  }, []);

  const onMouseDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    didDrag.current = false;
    dragOffset.current = {
      x: e.clientX - btnPos.current.x,
      y: e.clientY - btnPos.current.y,
    };
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    dragging.current = true;
    didDrag.current = false;
    dragOffset.current = {
      x: t.clientX - btnPos.current.x,
      y: t.clientY - btnPos.current.y,
    };
  };

  useEffect(() => {
    const move = (clientX, clientY) => {
      if (!dragging.current || !btnRef.current) return;
      didDrag.current = true;
      const x = Math.min(Math.max(clientX - dragOffset.current.x, 0), window.innerWidth - 56);
      const y = Math.min(Math.max(clientY - dragOffset.current.y, 0), window.innerHeight - 56);
      btnPos.current = { x, y };
      btnRef.current.style.left = x + 'px';
      btnRef.current.style.top = y + 'px';
    };
    const up = () => {
      if (!dragging.current) return;
      dragging.current = false;
      try { localStorage.setItem('dispatch_chat_btn_pos', JSON.stringify(btnPos.current)); } catch {}
      if (!didDrag.current) setOpen(o => !o);
    };
    const onMouseMove = (e) => move(e.clientX, e.clientY);
    const onTouchMove = (e) => move(e.touches[0].clientX, e.touches[0].clientY);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', up);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', up);
    };
  }, []);

  // messages / availableLoads / systemPrompt / contextLoaded now come
  // from AIDispatchContext (lifted above) so they survive client-side
  // route navigation but die on browser refresh. input, loading,
  // booking are transient per-action state and stay local.
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load context when chat opens for the first time
  useEffect(() => {
    if (open && !contextLoaded && user?.email) {
      buildContext();
    }
  }, [open, contextLoaded, user?.email]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const buildContext = async () => {
    setLoading(true);
    // Outer try/finally is the ultimate safety net: GUARANTEES loading
    // resets to false (and contextLoaded flips true) no matter what
    // synchronous bug fires below. Without this, a single uncaught
    // throw leaves the chat stuck on loading dots forever and users
    // can't even type a message (sendMessage early-returns when
    // loading is true).
    try {

    // Each context fetch wrapped independently so a single failure
    // (e.g. Settlement table missing in Supabase mode, $in query syntax
    // unsupported by compat client, market-loads upstream timeout)
    // doesn't take down the whole context build and leave the chat
    // stuck on loading dots forever. We fall back to sensible defaults
    // and still hand the AI a usable system prompt -- a degraded chat
    // that works > a "perfect" chat that hangs.
    let loads = [];
    try {
      loads = await client.entities.Load.filter({ status: 'Available' }, '-created_date', 50);
    } catch (e) {
      console.error('[AIDispatch] Load.filter failed (non-fatal):', e);
    }

    let settlements = [];
    try {
      settlements = await client.entities.Settlement.filter({ driver_email: user.email }, '-created_date', 100);
    } catch (e) {
      console.error('[AIDispatch] Settlement.filter failed (non-fatal):', e);
    }

    let marketLoads = [];
    try {
      marketLoads = await fetchMarketLoads(
        driverProfile?.address || 'Las Vegas, NV',
        // trailer_type drives market match post-split (see DriverFormModal).
        // Fall back to truck_type for drivers whose record still holds
        // the freight value there from the pre-refactor schema.
        driverProfile?.trailer_type || driverProfile?.truck_type || 'Reefer',
      );
    } catch (e) {
      console.error('[AIDispatch] fetchMarketLoads failed (non-fatal):', e);
    }

    const allLoads = [
      ...loads.map(l => ({ ...l, source: 'Company (Priority)' })),
      ...marketLoads,
    ];
    setAvailableLoads(allLoads);

    // Average rate per mile from last 90 days of settlements
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const recentSettlements = settlements.filter(s => {
      const d = new Date(s.settlement_date || s.created_date);
      return d >= cutoff && s.gross_revenue > 0;
    });
    const totalRevenue = recentSettlements.reduce((s, x) => s + (x.gross_revenue || 0), 0);

    const settlementLoadIds = recentSettlements.map(s => s.load_id).filter(Boolean);
    let settledLoads = [];
    if (settlementLoadIds.length > 0) {
      try {
        settledLoads = await client.entities.Load.filter({ id: { $in: settlementLoadIds } });
      } catch (e) {
        // Most likely the Supabase compat layer doesn't translate $in;
        // skip miles-aggregation and continue with a usable prompt.
        console.error('[AIDispatch] settled loads lookup failed (non-fatal):', e);
      }
    }
    const totalMiles = recentSettlements.reduce((sum, s) => {
      const matchedLoad = settledLoads.find(l => l.id === s.load_id);
      return sum + (matchedLoad?.miles || 0);
    }, 0);

    const avgRpm = recentSettlements.length > 0 && totalMiles > 0
      ? (totalRevenue / totalMiles).toFixed(2)
      : null;

    // Format loads for the prompt
    const loadsText = allLoads.map(l => {
      const rpm = l.miles && l.rate ? (l.rate / l.miles).toFixed(2) : 'N/A';
      const brokerInfo = l.source === 'Market' ? ` | Broker: ${l.broker_name} (${l.broker_phone})` : '';
      return `Load #${l.load_number} [${l.source}]: ${l.origin} → ${l.destination} | Rate: $${l.rate || 0} | Miles: ${l.miles || '?'} | RPM: $${rpm} | Pickup: ${l.pickup_date || 'TBD'} | Equipment: ${l.equipment_required} | Commodity: ${l.commodity || 'General'}${brokerInfo}`;
    }).join('\n');

    const driverName = driverProfile?.full_name || user?.full_name || user?.email;
    // Post-refactor: equipment_type for AI context = trailer (freight),
    // with fallback to truck_type for legacy records.
    const truckType = driverProfile?.trailer_type || driverProfile?.truck_type || 'Unknown';
    const homeBase = driverProfile?.address?.split(',')[1]?.trim() || driverProfile?.address || 'Unknown';

    const prompt = `You are a dispatch assistant for Pro Haul. You help owner-operators and fleet dispatchers find the best freight.

Driver: ${driverName}
Truck: ${truckType}
Home Base: ${homeBase}
${avgRpm ? `Driver's avg RPM (last 90 days): $${avgRpm}/mile` : 'No RPM history yet.'}
Completed loads (90 days): ${recentSettlements.length}

AVAILABLE LOADS RIGHT NOW (${allLoads.length} total — company + market):
${loadsText || 'No loads currently available.'}

Load source rules:
- Loads marked "Company (Priority)" are your carrier's own loads — these are pre-vetted and preferred. They are booked directly through the app.
- Loads marked "Market" are from the open market (Trulos). Always mention the broker name and phone number when recommending a market load so the driver can call to book. Market loads cannot be booked through the app.

Instructions: Recommend loads by number, always compare RPM to driver's average. Be brief and direct. Always prefer company loads when they are competitive. When a driver wants to book a company load, confirm the load number clearly. If a load number doesn't exist, say so. Never make up loads.`;

    setSystemPrompt(prompt);
    } catch (e) {
      console.error('[AIDispatch] buildContext failed (will continue with minimal prompt):', e);
      // Even on total failure, give the AI SOMETHING to work with so
      // the chat is usable rather than infinitely loading.
      setSystemPrompt(
        'You are a dispatch assistant for Pro Haul. Help the user with general dispatch and load questions. (Real-time load context is unavailable right now.)'
      );
    } finally {
      // ALWAYS reset loading + mark context loaded so the chat advances
      // into its usable state, even if every context query failed.
      setContextLoaded(true);
      setLoading(false);
    }
  };

  const sendMessage = async (text) => {
    const userMsg = text.trim();
    if (!userMsg || loading) return;
    setInput('');

    const newMessages = [...messages, { role: 'user', content: userMsg }];
    setMessages(newMessages);
    setLoading(true);

    // try/finally guarantees setLoading(false) fires even if invoke()
    // throws (e.g. 404 because anthropicChat isn't deployed, or
    // FunctionsHttpError on a 5xx). Without this, an unhandled throw
    // leaves the chat stuck on the typing-dots indicator forever and
    // the user has no idea what went wrong.
    try {
      const res = await client.functions.invoke('anthropicChat', {
        messages: newMessages,
        systemPrompt,
      });

      const reply = res?.data?.reply;
      if (reply) {
        setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: res?.data?.error || 'No response from AI.',
        }]);
      }
    } catch (e) {
      // Most common path here: anthropicChat isn't deployed yet (404),
      // ANTHROPIC_API_KEY isn't set (5xx), or transient network error.
      // Surface a useful message so the user sees what's wrong instead
      // of staring at infinite loading dots.
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `AI is unavailable right now. ${e?.message || 'Please try again in a moment.'}`,
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleBook = async (load) => {
    const myTrucks = await client.entities.Asset.filter({ assigned_driver_email: user.email, asset_type: 'Truck' }, '-created_date', 10);
    const { eligible, reasons } = checkBookingEligibility(driverProfile, user.email, myTrucks);
    if (!eligible) {
      toast.error(`You can't book this load yet — ${reasons.join(' ')}`, { duration: 6000 });
      return;
    }

    setBooking(load.id);
    // See LoadBoard.jsx's handleBook for why -- snapshots the unit this
    // driver is actually hauling with right now, instead of leaving
    // unit-level reports to infer it later from a possibly-stale current
    // truck assignment.
    const myActiveTruck = myTrucks.find(t => t.is_active !== false) || myTrucks[0];
    await client.entities.Load.update(load.id, {
      status: 'Booked',
      assigned_driver_email: user.email,
      assigned_driver_name: driverProfile?.full_name || user.full_name || user.email,
      truck_unit: myActiveTruck?.unit_number || undefined,
    });
    toast.success(`Load #${load.load_number} booked! Head to Active Load to manage it.`);
    // Remove from available
    setAvailableLoads(prev => prev.filter(l => l.id !== load.id));
    setBooking(null);
    // Notify Claude
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `✅ Load #${load.load_number} has been booked and assigned to you. Head to Active Load to manage it.`
    }]);
  };

  // (Removed: per-message localStorage persistence. Messages now live
  // in AIDispatchContext and reset on full browser refresh by design.)

  const clearChat = () => {
    setMessages([]);
  };

  const isFirstOpen = open && messages.length === 0 && !loading;



  return (
    <>
      {/* Floating toggle button — draggable */}
      <div
        ref={btnRef}
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        className={cn(
          "fixed z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center select-none",
          "bg-primary text-primary-foreground gold-glow cursor-grab active:cursor-grabbing",
          open ? "hidden md:flex" : "flex"
        )}
        style={{ left: window.innerWidth - 80, top: window.innerHeight - 80 }}
        aria-label="AI Dispatch Assistant"
      >
        <Sparkles className="w-6 h-6 pointer-events-none" />
      </div>

      {/* Chat panel — full screen on mobile, floating on desktop — rendered in a portal to escape backdrop-filter stacking contexts */}
      {open && createPortal(
        <div
          style={{ position: 'fixed', zIndex: 9999, display: 'flex', flexDirection: 'column' }}
          className="inset-0 rounded-none md:inset-auto md:rounded-2xl md:right-6 md:bottom-24 md:w-[400px] md:h-[580px] border border-border overflow-hidden shadow-2xl bg-background"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-white/3 flex-shrink-0">
            <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">Dispatch AI</p>
              <p className="text-xs text-muted-foreground">Powered by Claude</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              {messages.length > 0 && (
                <button onClick={clearChat} className="text-xs text-muted-foreground hover:text-foreground transition-colors px-2 py-1 rounded-lg hover:bg-white/10">
                  Clear
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3 min-h-0">
            {/* Loading context */}
            {loading && messages.length === 0 && (
              <div className="flex items-center gap-2 text-muted-foreground text-xs px-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Loading your driver context & available loads…
              </div>
            )}

            {/* Welcome + quick prompts */}
            {isFirstOpen && contextLoaded && (
              <div className="space-y-3">
                <div className="px-3.5 py-2.5 rounded-2xl rounded-tl-md bg-white/8 border border-border text-sm text-foreground leading-relaxed">
                  Hey {driverProfile?.full_name?.split(' ')[0] || 'there'}! 👋 I know your truck, your average RPM, and what's available right now. What are you looking for?
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {QUICK_PROMPTS.map(q => (
                    <button
                      key={q}
                      onClick={() => sendMessage(q)}
                      className="text-xs px-3 py-2 rounded-xl border border-primary/30 bg-primary/5 text-primary hover:bg-primary/15 transition-colors text-left leading-snug"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Conversation */}
            {messages.map((msg, i) => (
              <MessageBubble
                key={i}
                msg={msg}
                availableLoads={availableLoads}
                onBook={handleBook}
                booking={booking}
              />
            ))}

            {/* Typing indicator */}
            {loading && messages.length > 0 && (
              <div className="flex items-center gap-2 px-1">
                <div className="w-5 h-5 rounded-md bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-3 h-3 text-primary" />
                </div>
                <div className="flex gap-1 px-3 py-2.5 rounded-2xl bg-white/8 border border-border">
                  {[0, 1, 2].map(i => (
                    <span key={i} className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: `${i * 150}ms` }} />
                  ))}
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="px-3 py-3 border-t border-border flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/5 border border-border focus-within:border-primary/50 transition-colors">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } }}
                placeholder="Ask about loads…"
                disabled={loading || !contextLoaded}
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none disabled:opacity-50"
              />
              <button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || loading || !contextLoaded}
                className="text-primary hover:text-primary/80 transition-colors disabled:opacity-30"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}